import { AfterViewInit, Component, ElementRef, computed, effect, inject, signal, viewChild } from '@angular/core';
import { NgOptimizedImage } from '@angular/common';
import type { Map as LeafletMap, Marker } from 'leaflet';
import { RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  PublicProfileMapFilters,
  PublicProfileMapPoint,
  PublicProfileMapStatistics,
  ProfileAccountType,
} from '../../../../core/models';
import { REGION_CODES, SECTOR_CODES } from '../../../../core/constants/referentials';
import { organizationTypeLabelKey } from '../../../../shared/utils/organization-type.mapper';
import { PublicOrganizationsMapService } from '../../services/public-organizations-map';

type StructureTypeFilter = '' | 'STARTUP' | 'TPME' | 'PARTNER' | 'YOUNG';

const TYPE_FILTER_ORDER: Exclude<StructureTypeFilter, ''>[] = ['STARTUP', 'TPME', 'PARTNER', 'YOUNG'];

const TYPE_COLORS: Record<Exclude<StructureTypeFilter, ''>, string> = {
  STARTUP: '#2563EB',
  TPME: '#3A7A2B',
  PARTNER: '#0B2A44',
  YOUNG: '#7C3AED',
};

/**
 * UC-33 : cartographie publique de l’écosystème. Chaque profil public est représenté par un
 * marqueur individuel aux coordonnées retournées par profile-service. Ces coordonnées reflètent
 * actuellement la ville déclarée, pas une adresse ou un GPS précis : l’interface ne doit jamais
 * prétendre à une précision que l’utilisateur n’a pas fournie.
 */
const CAMEROON_CENTER: [number, number] = [6.5, 12.5];

/** A city is the source of truth whenever it is known. This prevents stale profile data such as
 * "Douala · Center" from being displayed or filtered as a geographical contradiction. */
const CITY_REGION: Record<string, string> = {
  DOUALA: 'LITTORAL', YAOUNDE: 'CENTER', BAFOUSSAM: 'WEST', BAMENDA: 'NORTH_WEST',
  BUEA: 'SOUTH_WEST', GAROUA: 'NORTH', MAROUA: 'FAR_NORTH', NGAOUNDERE: 'ADAMAOUA',
  BERTOUA: 'EAST', EBOLOWA: 'SOUTH',
};
const RESULT_PAGE_SIZE = 12;
/** Un seul appel large plutôt que 10 requêtes par région : le jeu de données réel (organisations
 * validées/publiques et jeunes/bénéficiaires visibles) reste modeste ; filtrer la région ensuite
 * côté client sur ce même résultat évite un aller-retour réseau par clic sans dupliquer de logique
 * métier (recherche et tri restent entièrement calculés par profile-service). */
const MAX_FETCH_SIZE = 500;

function normalizeCode(value: string | null | undefined): string {
  return (value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[\s-]+/g, '_')
    .toUpperCase();
}

@Component({
  selector: 'app-cartography',
  imports: [RouterLink, NgOptimizedImage, TranslatePipe],
  templateUrl: './cartography.html',
  styleUrl: './cartography.css',
})
export class Cartography implements AfterViewInit {
  private readonly publicOrganizationsMap = inject(PublicOrganizationsMapService);
  private readonly translate = inject(TranslateService);

  readonly typeFilterOrder = TYPE_FILTER_ORDER;
  readonly typeColors = TYPE_COLORS;

  readonly query = signal('');
  readonly typeFilter = signal<StructureTypeFilter>('');
  readonly regionFilter = signal('');
  readonly sectorFilter = signal('');
  readonly viewMode = signal<'carte' | 'liste'>('carte');
  readonly page = signal(0);

  readonly loading = signal(false);
  readonly error = signal(false);
  readonly allMatches = signal<PublicProfileMapPoint[]>([]);
  readonly totalMatches = signal(0);

  /** Valeurs de filtre réellement disponibles côté données publiques (évite les filtres qui ne renvoient jamais rien). */
  readonly availableFilters = signal<PublicProfileMapFilters | null>(null);
  readonly regionOptions = computed(() => Object.entries(REGION_CODES));
  readonly sectorOptions = computed(() => {
    const codes = this.availableFilters()?.sectorCodes;
    const entries = Object.entries(SECTOR_CODES);
    return codes ? entries.filter(([, code]) => codes.includes(code)) : entries;
  });

  /** Compteurs calculés sur les points normalisés affichés, pas sur des régions historiques erronées. */
  readonly mapStatistics = signal<PublicProfileMapStatistics | null>(null);
  readonly statsTotal = computed(() => this.totalMatches());
  readonly truncated = computed(() => this.totalMatches() > this.allMatches().length);

  /** Internal normalized grouping retained for compatibility; it is not rendered as a map counter. */
  readonly regionCounts = computed(() => {
    const counts = new Map<string, number>();
    for (const point of this.allMatches()) {
      const region = this.effectiveRegionCode(point);
      if (region) counts.set(region, (counts.get(region) ?? 0) + 1);
    }
    return counts;
  });

  readonly filteredResults = computed(() => {
    const region = this.regionFilter();
    return region ? this.allMatches().filter((point) => this.effectiveRegionCode(point) === region) : this.allMatches();
  });
  readonly publicResults = computed(() => this.filteredResults());
  readonly pagedResults = computed(() => {
    const start = this.page() * RESULT_PAGE_SIZE;
    return this.publicResults().slice(start, start + RESULT_PAGE_SIZE);
  });
  readonly totalPages = computed(() => Math.max(1, Math.ceil(this.publicResults().length / RESULT_PAGE_SIZE)));
  readonly fallbackPins = computed(() => [] as Array<{ regionCode: string; count: number; label: string; position: { top: string; left: string } }>);
  readonly hasMappableRegions = computed(() => false);

  readonly statsRegionsCount = computed(() => new Set(this.allMatches().map((point) => this.effectiveRegionCode(point)).filter(Boolean)).size);
  readonly statsSectorsCount = computed(() => new Set(this.allMatches().map((point) => point.sectorCode).filter(Boolean)).size);

  private readonly mapContainer = viewChild<ElementRef<HTMLDivElement>>('mapContainer');
  private map: LeafletMap | null = null;
  private readonly markersByProfile = new Map<string, Marker>();
  private destroyed = false;
  readonly mapReady = signal(false);
  readonly mapUnavailable = signal(false);
  private initScheduled = false;
  private mapInitializing = false;

  constructor() {
    effect(() => {
      this.scheduleMapInit();
    });
    effect(() => {
      const points = this.filteredResults();
      if (this.map) this.syncMarkers(points);
    });
    effect(() => {
      // Le conteneur reste dans le DOM (masqué) en mode "liste" pour ne pas détruire la carte ;
      // il faut lui redire sa taille quand il redevient visible, sinon les tuiles restent mal cadrées.
      if (this.viewMode() === 'carte') queueMicrotask(() => this.map?.invalidateSize());
    });

    this.loadFilters();
    this.loadStatistics();
    this.search();
  }

  ngAfterViewInit(): void {
    this.scheduleMapInit();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.map?.remove();
  }

  setViewMode(mode: 'carte' | 'liste'): void {
    this.viewMode.set(mode);
  }

  setType(type: StructureTypeFilter): void {
    this.typeFilter.set(type);
    this.page.set(0);
    this.search();
  }

  setRegionFilter(region: string): void {
    this.regionFilter.set(region);
    this.page.set(0);
    this.search();
  }

  setSectorFilter(sector: string): void {
    this.sectorFilter.set(sector);
    this.page.set(0);
    this.search();
  }

  applyQuery(): void {
    this.page.set(0);
    this.search();
  }

  resetFilters(): void {
    this.query.set('');
    this.typeFilter.set('');
    this.regionFilter.set('');
    this.sectorFilter.set('');
    this.page.set(0);
    this.search();
  }

  previousPage(): void {
    if (this.page() > 0) this.page.update((p) => p - 1);
  }

  nextPage(): void {
    if (this.page() + 1 < this.totalPages()) this.page.update((p) => p + 1);
  }

  initials(displayName: string): string {
    return displayName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join('');
  }

  phoneHref(phone: string): string {
    return phone.replace(/[^+\d]/g, '');
  }

  whatsappHref(phone: string): string {
    return 'https://wa.me/' + phone.replace(/\D/g, '');
  }

  regionLabel(regionCode: string | null): string {
    if (!regionCode) return '';
    return Object.values(REGION_CODES).includes(regionCode) ? this.translate.instant(`regions.${regionCode}`) : regionCode;
  }

  sectorLabel(sectorCode: string): string {
    return Object.values(SECTOR_CODES).includes(sectorCode) ? this.translate.instant(`sectors.${sectorCode}`) : sectorCode;
  }

  /** Type de chip/couleur pour un point de carte : YOUNG pour un jeune ou bénéficiaire anonymisé, sinon son type d'organisation. */
  pointTypeKey(point: PublicProfileMapPoint): Exclude<StructureTypeFilter, ''> {
    if (point.pointType === 'YOUNG') return 'YOUNG';
    return (point.organizationType as Exclude<StructureTypeFilter, ''> | null) ?? 'PARTNER';
  }

  colorFor(type: Exclude<StructureTypeFilter, ''>): string {
    return TYPE_COLORS[type] ?? TYPE_COLORS.PARTNER;
  }

  organizationTypeLabel(type: Exclude<StructureTypeFilter, ''>): string {
    if (type === 'YOUNG') return this.translate.instant('pages.cartography.youngTypeLabel');
    const key = organizationTypeLabelKey(type);
    return key ? this.translate.instant(key) : type;
  }

  focusRegion(regionCode: string): void {
    this.viewMode.set('carte');
    this.regionFilter.set(regionCode);
    this.page.set(0);
    this.search();
  }

  private loadFilters(): void {
    this.publicOrganizationsMap.profileFilters().subscribe({
      next: (filters) => this.availableFilters.set(filters),
      error: () => {
        // Silencieux : au pire les select conservent le référentiel complet (dégradation gracieuse).
      },
    });
  }

  private loadStatistics(): void {
    this.publicOrganizationsMap.profileStatistics().subscribe({
      next: (stats) => this.mapStatistics.set(stats),
      error: () => {
        // Silencieux : les cartes d'en-tête retombent sur totalMatches() via statsTotal().
      },
    });
  }

  private search(): void {
    this.loading.set(true);
    this.error.set(false);

    this.publicOrganizationsMap
      .profileMap({
        query: this.query().trim() || undefined,
        accountType: (this.typeFilter() || undefined) as ProfileAccountType | undefined,
        regionCode: undefined,
        sectorCode: this.sectorFilter() || undefined,
        size: MAX_FETCH_SIZE,
      })
      .subscribe({
        next: (page) => {
          this.allMatches.set(page.content);
          this.totalMatches.set(page.totalElements);
          this.loading.set(false);
          this.refreshMapViewport();
        },
        error: () => {
          this.loading.set(false);
          this.error.set(true);
        },
      });
  }

  private async initMap(container: HTMLDivElement): Promise<void> {
    if (this.map || this.mapInitializing || this.destroyed) return;
    this.mapInitializing = true;
    this.mapUnavailable.set(false);

    // Import dynamique : Leaflet manipule le DOM lui-même et ne doit tourner que côté navigateur.
    try {
      const L = await import('leaflet/dist/leaflet-src.esm.js');
      if (this.destroyed || this.map) return;

      this.map = L.map(container, {
        // zoomControl à part (ci-dessous, coin bas-droit) : le contrôle par défaut de Leaflet se
        // pose en haut-gauche, exactement où vit déjà le badge "N structure(s)" — les deux se
        // chevauchaient visuellement.
        zoomControl: false,
        preferCanvas: true,
        attributionControl: true,
      }).setView(CAMEROON_CENTER, 6);
      L.control.zoom({ position: 'bottomright' }).addTo(this.map);

      const planLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors',
        maxZoom: 19,
        errorTileUrl:
          'data:image/svg+xml;charset=UTF-8,' +
          encodeURIComponent(
            '<svg xmlns="http://www.w3.org/2000/svg" width="256" height="256"><rect width="256" height="256" fill="#eaf3ff"/><path d="M0 160C60 122 119 188 256 132" fill="none" stroke="#b8cee5" stroke-width="2"/><path d="M0 88C74 58 156 112 256 76" fill="none" stroke="#c8d8ea" stroke-width="2"/></svg>',
          ),
      });
      const satelliteLayer = L.tileLayer(
        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        { attribution: 'Tiles &copy; Esri', maxZoom: 19 },
      );
      const satelliteLabels = L.tileLayer(
        'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}',
        { maxZoom: 19 },
      );
      const satelliteHybrid = L.layerGroup([satelliteLayer, satelliteLabels]);

      planLayer.addTo(this.map);
      L.control.layers({ Plan: planLayer, Satellite: satelliteHybrid }, undefined, { position: 'topright' }).addTo(this.map);
      this.syncMarkers(this.filteredResults());
      this.mapReady.set(true);
      setTimeout(() => this.map?.invalidateSize(), 0);
      setTimeout(() => this.map?.invalidateSize(), 250);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      if (message.includes('already initialized')) {
        this.mapReady.set(true);
        return;
      }
      console.error('Leaflet public cartography initialization failed', error);
      if (!this.map) this.mapUnavailable.set(true);
    } finally {
      this.mapInitializing = false;
    }
  }

  private scheduleMapInit(): void {
    if (this.map || this.mapInitializing || this.initScheduled) return;
    const container = this.mapContainer()?.nativeElement;
    if (!container) return;

    this.initScheduled = true;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.initScheduled = false;
        const current = this.mapContainer()?.nativeElement;
        if (!current || this.map || this.destroyed) return;
        this.initMap(current);
      });
    });
  }

  private refreshMapViewport(): void {
    if (!this.map) return;
    setTimeout(() => this.map?.invalidateSize(), 0);
    setTimeout(() => this.map?.invalidateSize(), 180);
  }

  private async syncMarkers(points: PublicProfileMapPoint[]): Promise<void> {
    if (!this.map) return;
    const L = await import("leaflet/dist/leaflet-src.esm.js");
    if (this.destroyed || !this.map) return;

    for (const marker of this.markersByProfile.values()) marker.remove();
    this.markersByProfile.clear();

    for (const point of points) {
      if (point.latitude == null || point.longitude == null) continue;
      const color = this.colorFor(this.pointTypeKey(point));
      const icon = L.divIcon({
        html: "<span style=\"display:block;width:16px;height:16px;border-radius:50%;background:" + color + ";border:3px solid #fff;box-shadow:0 1px 5px rgba(11,42,68,.45)\"></span>",
        className: "",
        iconSize: [16, 16],
        iconAnchor: [8, 8],
      });
      const marker = L.marker([point.latitude, point.longitude], { icon, title: point.label }).addTo(this.map);
      const location = [point.city, this.regionLabel(this.effectiveRegionCode(point))].filter(Boolean).join(" · ");
      const tooltip = this.markerTooltip(point, location);
      marker.bindTooltip(tooltip, { direction: "top", offset: [0, -8], opacity: 0.98 });
      marker.bindPopup(tooltip);
      this.markersByProfile.set(point.profileId ?? point.label + "-" + point.latitude + "-" + point.longitude, marker);
    }
  }

  private effectiveRegionCode(point: PublicProfileMapPoint): string | null {
    return CITY_REGION[normalizeCode(point.city)] ?? point.regionCode;
  }

  regionFor(point: PublicProfileMapPoint): string | null {
    return this.effectiveRegionCode(point);
  }

  private markerTooltip(point: PublicProfileMapPoint, location: string): string {
    const description = point.shortDescription ? "<br><span>" + this.escapeHtml(point.shortDescription) + "</span>" : "";
    const phone = point.phone ? "<br><strong>" + this.escapeHtml(point.phone) + "</strong>" : "";
    return "<strong>" + this.escapeHtml(point.label) + "</strong><br><span>" + this.escapeHtml(location) + "</span>" + description + phone;
  }

  private escapeHtml(value: string): string {
    return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }
}
